/**
 * useTTS — React hook for Text-to-Speech audio playback.
 *
 * Fetches TTS audio from the backend and plays it via HTML Audio.
 * Only one message can play at a time.
 *
 * Extended for Story 7.2: audio queue with sequential playback,
 * preloading, and interruption support.
 *
 * Story v19-1-3: Stable audio element — reuse a single HTMLAudioElement
 * instead of creating new Audio() objects per sentence. This lets
 * useVisemeFromAudio connect createMediaElementSource() once and rely on
 * play/pause/ended events for lip sync timing.
 */

import React, { useState, useCallback, useRef } from 'react';
import { expertApi } from '../services/expertApi';

export interface UseTTSReturn {
  /** Start speaking text for a given message. Stops any previous playback. */
  speak: (text: string, messageId: number, voice?: string) => Promise<void>;
  /** Stop current playback. */
  stop: () => void;
  /** Whether audio is currently playing. */
  isPlaying: boolean;
  /** Whether audio is currently loading (fetching from API). */
  isLoading: boolean;
  /** The message ID currently playing/loading, or null. */
  currentMessageId: number | null;
  /** Queue a sentence for sequential TTS playback (Story 7.2). */
  queueSentence: (text: string, messageId: number, voice?: string) => void;
  /** Clear the audio queue and stop playback. */
  clearQueue: () => void;
  /** Whether the queue is actively playing sentences. */
  isQueuePlaying: boolean;
  /**
   * Ref to the stable HTMLAudioElement used for all TTS playback.
   * Exposed for Web Audio API integration (Story v4-9-1: 3D avatar lip sync).
   * Read-only — do not mutate. The element is reused across sentences so
   * createMediaElementSource() only needs to be called once.
   */
  audioRef: React.RefObject<HTMLAudioElement>;
}

interface QueueItem {
  text: string;
  messageId: number;
  voice?: string;
}

export function useTTS(): UseTTSReturn {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentMessageId, setCurrentMessageId] = useState<number | null>(null);
  const [isQueuePlaying, setIsQueuePlaying] = useState(false);
  // Stable audio element — created once, reused for all sentences.
  // createMediaElementSource() (for lip sync) only needs to be called once per element.
  const audioRef = useRef<HTMLAudioElement>(new Audio());
  const urlRef = useRef<string | null>(null);
  const queueRef = useRef<QueueItem[]>([]);
  const processingRef = useRef(false);
  const preloadedRef = useRef<{ url: string; messageId: number } | null>(null);
  /**
   * Generation counter (M2 fix): incremented on every clearQueue() call.
   * processQueue() captures its generation at entry and bails out of the
   * loop whenever the counter has advanced, preventing re-entrant loops
   * from stomping on each other after a fade-out race.
   */
  const clearGenerationRef = useRef(0);

  const cleanup = useCallback(() => {
    const audio = audioRef.current;
    audio.pause();
    audio.removeAttribute('src');
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    cleanup();
    setIsPlaying(false);
    setIsLoading(false);
    setCurrentMessageId(null);
  }, [cleanup]);

  const speak = useCallback(
    async (text: string, messageId: number, voice?: string) => {
      // Stop any previous playback
      stop();

      setIsLoading(true);
      setCurrentMessageId(messageId);

      try {
        const response = await expertApi.ttsSpeak(text, voice);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        urlRef.current = url;

        const audio = audioRef.current;
        audio.src = url;
        audio.load(); // reset element state before play (required when reusing stable element)

        audio.onplay = () => {
          setIsLoading(false);
          setIsPlaying(true);
        };

        audio.onended = () => {
          setIsPlaying(false);
          setCurrentMessageId(null);
          cleanup();
        };

        audio.onerror = () => {
          setIsPlaying(false);
          setIsLoading(false);
          setCurrentMessageId(null);
          cleanup();
        };

        await audio.play();
      } catch {
        setIsPlaying(false);
        setIsLoading(false);
        setCurrentMessageId(null);
        cleanup();
      }
    },
    [stop, cleanup]
  );

  // ─── Audio Queue (Story 7.2) ───────────────────────────────────

  const processQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;

    // Capture the generation at the time this loop starts.
    // If clearQueue() is called while we are mid-loop, clearGenerationRef
    // will be incremented and we will exit cleanly on the next iteration.
    const myGeneration = clearGenerationRef.current;

    while (queueRef.current.length > 0) {
      // M2 fix: bail out if a clearQueue() has fired since this loop started
      if (clearGenerationRef.current !== myGeneration) {
        processingRef.current = false;
        return;
      }

      const item = queueRef.current.shift()!;
      setCurrentMessageId(item.messageId);
      setIsLoading(true);
      setIsPlaying(false);

      try {
        let audioUrl: string;

        // Use preloaded audio if available for this item
        if (preloadedRef.current && preloadedRef.current.messageId === item.messageId) {
          audioUrl = preloadedRef.current.url;
          preloadedRef.current = null;
        } else {
          const response = await expertApi.ttsSpeak(item.text, item.voice);
          const blob = await response.blob();
          audioUrl = URL.createObjectURL(blob);
        }

        // M2 fix: check generation after the async fetch — clearQueue may have
        // fired while we were awaiting the network response
        if (clearGenerationRef.current !== myGeneration) {
          URL.revokeObjectURL(audioUrl);
          processingRef.current = false;
          return;
        }

        urlRef.current = audioUrl;

        // Preload next sentence while current plays
        const nextItem = queueRef.current[0];
        if (nextItem) {
          expertApi.ttsSpeak(nextItem.text, nextItem.voice).then(async (res) => {
            const blob = await res.blob();
            preloadedRef.current = {
              url: URL.createObjectURL(blob),
              messageId: nextItem.messageId,
            };
          }).catch(() => { /* preload failure is non-fatal */ });
        }

        const audio = audioRef.current;
        audio.src = audioUrl;
        audio.load(); // reset element state before play (required when reusing stable element)

        await new Promise<void>((resolve, reject) => {
          audio.onplay = () => {
            setIsLoading(false);
            setIsPlaying(true);
          };
          audio.onended = () => {
            cleanup();
            resolve();
          };
          audio.onerror = () => {
            cleanup();
            reject(new Error('Audio playback error'));
          };
          audio.play().catch(reject);
        });
      } catch {
        cleanup();
      }
    }

    processingRef.current = false;
    setIsPlaying(false);
    setIsLoading(false);
    setIsQueuePlaying(false);
    setCurrentMessageId(null);
  }, [cleanup]);

  const queueSentence = useCallback(
    (text: string, messageId: number, voice?: string) => {
      queueRef.current.push({ text, messageId, voice });
      setIsQueuePlaying(true);
      setCurrentMessageId(messageId);
      processQueue();
    },
    [processQueue]
  );

  const clearQueue = useCallback(() => {
    // M2 fix: increment the generation counter FIRST so any in-flight
    // processQueue() loop will detect the cancellation on its next iteration
    // and exit before starting new audio, preventing re-entrant playback.
    clearGenerationRef.current += 1;

    queueRef.current = [];

    // Clean preloaded
    if (preloadedRef.current) {
      URL.revokeObjectURL(preloadedRef.current.url);
      preloadedRef.current = null;
    }

    // Fade out current audio
    const audio = audioRef.current;
    if (audio.src) {
      const fadeInterval = setInterval(() => {
        if (audio.volume > 0.1) {
          audio.volume = Math.max(0, audio.volume - 0.3);
        } else {
          clearInterval(fadeInterval);
          audio.pause();
        }
      }, 33); // ~100ms total fade (3 steps)
      // Safety: force stop after 150ms
      setTimeout(() => {
        clearInterval(fadeInterval);
        audio.pause();
        audio.removeAttribute('src');
        if (urlRef.current) {
          URL.revokeObjectURL(urlRef.current);
          urlRef.current = null;
        }
      }, 150);
    }

    processingRef.current = false;
    setIsQueuePlaying(false);
    setIsPlaying(false);
    setIsLoading(false);
    setCurrentMessageId(null);
  }, []);

  return { speak, stop, isPlaying, isLoading, currentMessageId, queueSentence, clearQueue, isQueuePlaying, audioRef };
}
